import { useCallback, useMemo } from "react";

import { Sidebar as SidebarLib } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import sidebarIcon from "../../assets/images/sidebar-icon.svg";
import type { WebExport } from "../../hooks/useWebExport";
import { Sidebar } from "../Sidebar/Sidebar";
import { Toolbar } from "../Toolbar/Toolbar";
import { ToolButton } from "../ToolButton/ToolButton";

import style from "./ModelViewerNavbar.module.css";

type ModelViewerNavbarProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  webExport: WebExport | undefined;
  isDrawingsLayout: boolean;
  isInDrawing: boolean;
  isCommentingEnabled: boolean;
};

const ModelViewerNavbar: React.FC<ModelViewerNavbarProps> = ({
  open,
  webExport,
  setOpen,
  isDrawingsLayout,
  isInDrawing,
  isCommentingEnabled,
}) => {
  const { viewLink } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const sortedDrawings = useMemo(() => {
    const drawings = webExport?.drawingsDownload ?? [];

    return [...drawings].sort((a, b) => (a.title > b.title ? 1 : a.title < b.title ? -1 : 0));
  }, [webExport?.drawingsDownload]);

  const navigationHandler = useCallback(() => {
    if (window.innerWidth < 481) {
      setOpen(!open);
    }
  }, [open, setOpen]);

  return (
    <Sidebar
      title="Project Outline"
      position="left"
      isOpen={open}
      onClose={() => setOpen(false)}
      controlButtons={
        <div className={style.sidebarWrapper}>
          {isDrawingsLayout && (
            <ToolButton
              alt="Project Outline"
              title="Project Outline"
              icon={sidebarIcon}
              onClick={() => setOpen?.(!open)}
              isActive={open}
            />
          )}
          {!isInDrawing && isCommentingEnabled && <Toolbar />}
        </div>
      }
    >
      <div className={style.navbarContent}>
        <SidebarLib.Header>Model</SidebarLib.Header>
        <SidebarLib.Item
          selected={`/v/${viewLink}` === location.pathname}
          onClick={() => {
            navigate(`/v/${viewLink}` + search);
            navigationHandler();
          }}
          className={classNames(
            style.sidebarItem,
            `/v/${viewLink}` === location.pathname && style.active
          )}
        >
          <span className={classNames(style.item)}>{webExport?.title}</span>
        </SidebarLib.Item>
        <SidebarLib.Header>Drawings</SidebarLib.Header>
        {sortedDrawings.map((drawing) => (
          <SidebarLib.Item
            selected={`/v/${viewLink}/drawings/${drawing.drawingId}` === location.pathname}
            onClick={() => {
              navigate(`/v/${viewLink}/drawings/${drawing.drawingId}` + search);
              navigationHandler();
            }}
            key={drawing.drawingId}
            className={classNames(
              style.sidebarItem,
              `/v/${viewLink}/drawings/${drawing.drawingId}` === location.pathname && style.active
            )}
          >
            <span className={classNames(style.item)}>{drawing.title}</span>
          </SidebarLib.Item>
        ))}
      </div>
    </Sidebar>
  );
};

export default ModelViewerNavbar;
